import {
  useState,
  useEffect,
  useCallback,
  useContext,
  useRef,
  useLayoutEffect,
} from "react";
import { createPortal } from "react-dom";
import { CellContextMenuEvent } from "ag-grid-community";
import { IconType } from "react-icons";
import { FiCopy } from "react-icons/fi";
import { SnackbarContext } from "@components/constant_components/providers/SnackbarProvider";

/* to specify the columns to enable context menu on, pass enabledColumns array with column field names, inside the AgGridContextMenu call, for example:
  const { handleCellContextMenu, ContextMenuComponent } = AgGridContextMenu({
    enabledColumns: ["email", "phone", "contractor_id"],
  });

  or

  const { handleCellContextMenu, ContextMenuComponent } = AgGridContextMenu({
  shouldShowMenu: (params) => {
    // Only show for columns that contain text data
    return params.column.getColId() !== "is_active";
  }
});
*/

/* to specify the custom menu items, pass menuItems array with label, onClick handler, optional icon and optional disabled function, inside the AgGridContextMenu call, for example:
const {
    handleCellContextMenu,
    ContextMenuComponent,
    handleCopyFromContextMenu,
  } = AgGridContextMenu({
    menuItems: [
      {
        label: "Copy Cell Value",
        icon: FiCopy,
        onClick: () => handleCopyFromContextMenu(),
        disabled: (value: string) => !value.length,
      },
      {
        label: "Custom Action",
        icon: FiEdit,
        onClick: (value, rowData) => {
          // console.log("Cell value:", value);
          // console.log("Full row data:", rowData);
        },
      },
    ],
  });
*/

interface AgGridContextMenuProps {
  /** Optional custom menu items. If not provided, shows default "Copy Cell Value" */
  menuItems?: Array<{
    label: string;
    icon?: IconType;
    onClick: (value: string, rowData?: any) => void;
    disabled?: (value: string, rowData?: any) => boolean;
  }>;
  /**
   * Optional array of column field names where context menu should be enabled.
   * If not provided, context menu appears on all columns.
   */
  enabledColumns?: string[];
  /**
   * Optional callback to determine if context menu should show for a specific column.
   * Takes precedence over enabledColumns if both are provided.
   */
  shouldShowMenu?: (params: CellContextMenuEvent) => boolean;
}

export function AgGridContextMenu({
  menuItems,
  enabledColumns,
  shouldShowMenu,
}: AgGridContextMenuProps) {
  const { triggerSnackbar } = useContext(SnackbarContext);
  const [contextMenuState, setContextMenuState] = useState<{
    visible: boolean;
    value: string;
    rowData: any;
    mouseX: number;
    mouseY: number;
  }>({
    visible: false,
    value: "",
    rowData: null,
    mouseX: 0,
    mouseY: 0,
  });
  const menuRef = useRef<HTMLDivElement | null>(null);
  const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0 });

  const copyTextToClipboard = useCallback(
    async (text: string) => {
      if (!text.length) {
        return;
      }

      try {
        if (navigator?.clipboard?.writeText) {
          await navigator.clipboard.writeText(text);
        } else {
          const textarea = document.createElement("textarea");
          textarea.value = text;
          textarea.style.position = "fixed";
          textarea.style.opacity = "0";
          document.body.appendChild(textarea);
          textarea.focus();
          textarea.select();
          document.execCommand("copy");
          document.body.removeChild(textarea);
        }
        triggerSnackbar("Cell value copied to clipboard.");
      } catch (error) {
        console.error("Failed to copy cell value", error);
        triggerSnackbar("Unable to copy cell value.");
      }
    },
    [triggerSnackbar]
  );

  useEffect(() => {
    const hideMenu = () => {
      setContextMenuState((prev) =>
        prev.visible ? { ...prev, visible: false } : prev
      );
    };

    window.addEventListener("click", hideMenu);
    window.addEventListener("scroll", hideMenu, true);

    return () => {
      window.removeEventListener("click", hideMenu);
      window.removeEventListener("scroll", hideMenu, true);
    };
  }, []);

  useLayoutEffect(() => {
    if (!contextMenuState.visible) {
      return;
    }

    const menuEl = menuRef.current;
    if (!menuEl) {
      return;
    }

    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    const rect = menuEl.getBoundingClientRect();
    const padding = 8;
    const cursorOffset = 2;

    // Prefer opening to the right/down; switch to left/up when near viewport edge.
    let nextLeft = contextMenuState.mouseX + cursorOffset;
    let nextTop = contextMenuState.mouseY + cursorOffset;

    if (nextLeft + rect.width + padding > viewportWidth) {
      nextLeft = contextMenuState.mouseX - rect.width - cursorOffset;
    }

    if (nextTop + rect.height + padding > viewportHeight) {
      nextTop = contextMenuState.mouseY - rect.height - cursorOffset;
    }

    // Final safety clamp for very small viewports.
    nextLeft = Math.min(nextLeft, viewportWidth - rect.width - padding);
    nextTop = Math.min(nextTop, viewportHeight - rect.height - padding);

    nextLeft = Math.max(padding, nextLeft);
    nextTop = Math.max(padding, nextTop);

    setMenuPosition({ top: nextTop, left: nextLeft });
  }, [
    contextMenuState.visible,
    contextMenuState.mouseX,
    contextMenuState.mouseY,
  ]);

  const handleCellContextMenu = useCallback(
    (params: CellContextMenuEvent) => {
      const mouseEvent = params.event as MouseEvent | null;
      if (!mouseEvent) {
        return;
      }

      // Check if menu should be shown for this column
      if (shouldShowMenu && !shouldShowMenu(params)) {
        return;
      }

      if (
        enabledColumns &&
        !enabledColumns.includes(params.column.getColId())
      ) {
        return;
      }

      mouseEvent.preventDefault();
      const value =
        params.value === null || params.value === undefined
          ? ""
          : String(params.value);
      setMenuPosition({ top: mouseEvent.clientY, left: mouseEvent.clientX });
      setContextMenuState({
        visible: true,
        value,
        rowData: params.data,
        mouseX: mouseEvent.clientX,
        mouseY: mouseEvent.clientY,
      });
    },
    [shouldShowMenu, enabledColumns]
  );

  const handleCopyFromContextMenu = useCallback(() => {
    setContextMenuState((prev) => ({ ...prev, visible: false }));
    if (contextMenuState.value.length) {
      void copyTextToClipboard(contextMenuState.value);
    } else {
      triggerSnackbar("Nothing to copy from this cell.");
    }
  }, [contextMenuState.value, copyTextToClipboard, triggerSnackbar]);

  const defaultMenuItems: Array<{
    label: string;
    icon?: IconType;
    onClick: (value: string, rowData?: any) => void;
    disabled?: (value: string, rowData?: any) => boolean;
  }> = [
    {
      label: "Copy Cell Value",
      icon: FiCopy,
      onClick: handleCopyFromContextMenu,
      disabled: (value: string) => !value.length,
    },
  ];

  const items = menuItems || defaultMenuItems;

  const menuContent = contextMenuState.visible ? (
    <div
      ref={menuRef}
      className="tw-fixed tw-z-[4000] tw-bg-white tw-border tw-border-gray-200 tw-rounded tw-shadow-lg tw-p-2 tw-min-w-[160px]"
      style={{
        top: menuPosition.top,
        left: menuPosition.left,
      }}
    >
      {items.map((item, index) => {
        const Icon = item.icon;
        return (
          <button
            key={index}
            className="tw-group tw-w-full tw-text-left tw-text-xs tw-text-gray-800 tw-px-2 tw-py-1 tw-border tw-border-transparent hover:tw-border-gray-400 hover:tw-bg-blue-50 tw-rounded disabled:tw-text-gray-400 disabled:hover:tw-bg-transparent tw-flex tw-items-center tw-gap-2"
            onClick={() =>
              item.onClick(contextMenuState.value, contextMenuState.rowData)
            }
            disabled={
              item.disabled
                ? item.disabled(
                    contextMenuState.value,
                    contextMenuState.rowData
                  )
                : false
            }
          >
            {Icon && (
              <Icon className="tw-w-4 tw-h-4 tw-flex-shrink-0  group-hover:tw-text-orange-600" />
            )}
            <span>{item.label}</span>
          </button>
        );
      })}
    </div>
  ) : null;

  return {
    contextMenuState,
    handleCellContextMenu,
    handleCopyFromContextMenu,
    ContextMenuComponent:
      typeof document !== "undefined" && menuContent
        ? createPortal(menuContent, document.body)
        : null,
  };
}
